import { useState, useEffect, useRef } from 'react';
import { X, Camera, MapPin, User, Building, Mail, Phone, Briefcase, Upload, Check, AlertCircle, Loader2, Save, FileText, Star, Sparkles, Trash2 } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { AddressAutocomplete, Button } from '../ui';
import { updateLocalContact, removeUserContact } from '../../services/userContactsService';
import { searchOccupationsEsco, selectOccupation } from '../../services/occupationsService';
import { enhanceTextWithAI } from '../../services/geminiService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be/rest/cabaza';

/**
 * Modal for editing an existing contact
 * Features:
 * - Tab 1: Basic info (firstName, lastName, email, phone, company, phoneBureau, sexe)
 * - Tab 2: Address with Google Maps autocomplete (all address fields)
 * - Tab 3: Professional info (profileType, occupation, tvaNum, bank, employment status, reputation)
 * - Tab 4: Notes with AI enhancement
 */
const EditContactModal = ({ isOpen, onClose, contact, currentUser: propCurrentUser, onContactUpdated, onContactDeleted }) => {
    const { t, language } = useTranslation();

    // Get current user from prop or localStorage
    const currentUser = propCurrentUser || (() => {
        try {
            const cabazaUser = JSON.parse(localStorage.getItem('cabaza_user') || '{}');
            return {
                id: cabazaUser.id || parseInt(localStorage.getItem('userId'), 10),
                email: cabazaUser.email || localStorage.getItem('userEmail')
            };
        } catch (e) {
            return { id: parseInt(localStorage.getItem('userId'), 10) };
        }
    })();

    // Load Google Maps API
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const isGoogleMapsLoaded = useGoogleMaps(googleMapsApiKey);

    // State
    const [activeTab, setActiveTab] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Profile types state
    const [profileTypes, setProfileTypes] = useState([]);
    const [isLoadingProfileTypes, setIsLoadingProfileTypes] = useState(false);

    // Occupation search state
    const [occupationQuery, setOccupationQuery] = useState('');
    const [occupationResults, setOccupationResults] = useState([]);
    const [isSearchingOccupation, setIsSearchingOccupation] = useState(false);
    const [showOccupationResults, setShowOccupationResults] = useState(false);
    const [selectedOccupation, setSelectedOccupation] = useState(null);

    // AI enhancement state
    const [isEnhancingDescription, setIsEnhancingDescription] = useState(false);

    // Form data with all fields
    const [formData, setFormData] = useState({
        // Tab 1: Basic info
        firstName: '',
        lastName: '',
        sexe: '',
        email: '',
        phone: '',
        company: '',
        phoneBureau: '',
        // Tab 2: Address
        googleAddress: '',
        street: '',
        number: '',
        box: '',
        postalCode: '',
        city: '',
        country: '',
        countryCode: '',
        latitude: null,
        longitude: null,
        googlePlaceId: '',
        googleUrl: '',
        nationality: '',
        // Tab 3: Professional
        profileTypeId: '',
        occupationId: null,
        occupationLabel_fr: '',
        occupationLabel_en: '',
        employmentStatus: true,
        tvaNum: '',
        bankName: '',
        bankNum: '',
        reputation: 0,
        // Tab 4: Notes
        description: ''
    });

    // Sexe options
    const sexeOptions = [
        { value: '', label: t.contacts?.fields?.genderOptions?.unspecified || 'Not specified' },
        { value: 'M', label: t.contacts?.fields?.genderOptions?.male || 'Male' },
        { value: 'F', label: t.contacts?.fields?.genderOptions?.female || 'Female' },
        { value: 'O', label: t.contacts?.fields?.genderOptions?.other || 'Other' }
    ];

    // Load profile types
    const loadProfileTypes = async () => {
        setIsLoadingProfileTypes(true);
        try {
            const response = await fetch(`${API_BASE_URL}/profiletypes`);
            const data = await response.json();
            const types = data.data ?? data.DATA ?? data ?? [];
            setProfileTypes(Array.isArray(types) ? types : []);
        } catch (error) {
            console.error('Error loading profile types:', error);
            setProfileTypes([]);
        } finally {
            setIsLoadingProfileTypes(false);
        }
    };

    // Load contact data when modal opens
    useEffect(() => {
        if (isOpen && contact) {
            loadProfileTypes();

            // Pre-fill form with contact data
            setFormData({
                firstName: contact.firstName || '',
                lastName: contact.lastName || '',
                sexe: contact.sexe || '',
                email: contact.email || '',
                phone: contact.phone || '',
                company: contact.company || '',
                phoneBureau: contact.phoneBureau || '',
                googleAddress: contact.googleAddress || '',
                street: contact.street || '',
                number: contact.number || '',
                box: contact.box || '',
                postalCode: contact.postalCode || '',
                city: contact.city || '',
                country: contact.country || '',
                countryCode: contact.countryCode || '',
                latitude: contact.latitude || null,
                longitude: contact.longitude || null,
                googlePlaceId: contact.googlePlaceId || '',
                googleUrl: contact.googleUrl || '',
                nationality: contact.nationality || '',
                profileTypeId: contact.profileTypeId || '',
                occupationId: contact.occupationId || null,
                occupationLabel_fr: contact.occupationLabel_fr || '',
                occupationLabel_en: contact.occupationLabel_en || '',
                employmentStatus: contact.employmentStatus !== false,
                tvaNum: contact.tvaNum || '',
                bankName: contact.bankName || '',
                bankNum: contact.bankNum || '',
                reputation: contact.reputation || 0,
                description: contact.description || contact.notes || ''
            });

            // Set occupation query if exists
            if (contact.occupationLabel_fr || contact.occupationLabel_en) {
                setOccupationQuery(language === 'fr' ? contact.occupationLabel_fr : contact.occupationLabel_en);
                setSelectedOccupation({ id: contact.occupationId });
            } else {
                setOccupationQuery('');
                setSelectedOccupation(null);
            }

            setActiveTab(0);
            setSaveMessage({ type: '', text: '' });
            setShowDeleteConfirm(false);
        }
    }, [isOpen, contact, language]);

    // Search occupations via ESCO API
    const handleOccupationSearch = async (query) => {
        setOccupationQuery(query);
        if (query.length < 2) {
            setOccupationResults([]);
            setShowOccupationResults(false);
            return;
        }
        setIsSearchingOccupation(true);
        setShowOccupationResults(true);
        try {
            const results = await searchOccupationsEsco(query, language, 10);
            setOccupationResults(results || []);
        } catch (error) {
            console.error('Occupation search error:', error);
            setOccupationResults([]);
        } finally {
            setIsSearchingOccupation(false);
        }
    };

    // Select an occupation
    const handleSelectOccupation = async (occupation) => {
        try {
            const savedOccupation = await selectOccupation(occupation);
            setSelectedOccupation(savedOccupation);
            setFormData(prev => ({
                ...prev,
                occupationId: savedOccupation.id,
                occupationLabel_fr: savedOccupation.name_fr || occupation.displayLabel,
                occupationLabel_en: savedOccupation.name_en || occupation.displayLabel
            }));
            setOccupationQuery(occupation.displayLabel || occupation.title);
            setShowOccupationResults(false);
        } catch (error) {
            console.error('Error selecting occupation:', error);
        }
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle address selection from Google autocomplete
    const handleAddressSelect = (addressData) => {
        setFormData(prev => ({
            ...prev,
            googleAddress: addressData.formatted_address || addressData.address || '',
            street: addressData.street || '',
            number: addressData.number || '',
            postalCode: addressData.postalCode || addressData.zip || '',
            city: addressData.city || '',
            country: addressData.country || '',
            countryCode: addressData.countryCode || '',
            latitude: addressData.latitude || null,
            longitude: addressData.longitude || null,
            googlePlaceId: addressData.placeId || '',
            googleUrl: addressData.url || ''
        }));
    };

    // Handle coordinates from address autocomplete
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    };

    // Handle address components from Google
    const handleAddressComponentsChange = (components) => {
        if (components) {
            setFormData(prev => ({
                ...prev,
                street: components.street || prev.street,
                number: components.number || prev.number,
                postalCode: components.zip || components.postalCode || prev.postalCode,
                city: components.city || prev.city,
                country: components.country || prev.country,
                countryCode: components.countryCode || prev.countryCode
            }));
        }
    };

    // Enhance description with AI
    const handleEnhanceDescription = async () => {
        if (!formData.description.trim()) return;
        setIsEnhancingDescription(true);
        try {
            const enhanced = await enhanceTextWithAI(formData.description, language);
            if (enhanced) {
                setFormData(prev => ({ ...prev, description: enhanced }));
            }
        } catch (error) {
            console.error('Error enhancing description:', error);
        } finally {
            setIsEnhancingDescription(false);
        }
    };

    // Save contact
    const handleSave = async () => {
        const contactLocalUuid = contact?.localuuid || contact?.LOCALUUID;
        if (!currentUser?.id || !contactLocalUuid) {
            console.error('Cannot save: missing currentUser.id or contact.localuuid', { userId: currentUser?.id, localuuid: contactLocalUuid });
            return;
        }

        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            const updates = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                sexe: formData.sexe,
                email: formData.email,
                phone: formData.phone,
                company: formData.company,
                phoneBureau: formData.phoneBureau,
                googleAddress: formData.googleAddress,
                street: formData.street,
                number: formData.number,
                box: formData.box,
                postalCode: formData.postalCode,
                city: formData.city,
                country: formData.country,
                countryCode: formData.countryCode,
                latitude: formData.latitude,
                longitude: formData.longitude,
                googlePlaceId: formData.googlePlaceId,
                googleUrl: formData.googleUrl,
                nationality: formData.nationality,
                profileTypeId: formData.profileTypeId || null,
                occupationId: formData.occupationId,
                occupationLabel_fr: formData.occupationLabel_fr,
                occupationLabel_en: formData.occupationLabel_en,
                employmentStatus: formData.employmentStatus,
                tvaNum: formData.tvaNum,
                bankName: formData.bankName,
                bankNum: formData.bankNum,
                reputation: formData.reputation,
                notes: formData.description,
                description: formData.description
            };

            const success = updateLocalContact(currentUser.id, contactLocalUuid, updates);

            if (success) {
                setSaveMessage({
                    type: 'success',
                    text: t.contacts?.contactUpdated || 'Contact updated successfully!'
                });
                onContactUpdated?.({ ...contact, ...updates });
                window.dispatchEvent(new CustomEvent('contactUpdated', { detail: { ...contact, ...updates } }));
                setTimeout(() => onClose(), 1500);
            } else {
                throw new Error('Failed to update contact');
            }
        } catch (error) {
            console.error('Update contact error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (t.contacts?.errorUpdating || 'Error updating contact')
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Delete contact
    const handleDelete = async () => {
        if (!currentUser?.id || !contact?.id) return;

        setIsDeleting(true);
        setSaveMessage({ type: '', text: '' });

        try {
            const success = await removeUserContact(currentUser.id, contact.id);

            if (success) {
                setSaveMessage({
                    type: 'success',
                    text: t.contacts?.contactDeleted || 'Contact deleted!'
                });
                onContactDeleted?.(contact);
                window.dispatchEvent(new CustomEvent('contactDeleted', { detail: contact }));
                setTimeout(() => onClose(), 1000);
            } else {
                throw new Error('Failed to delete contact');
            }
        } catch (error) {
            console.error('Delete contact error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (t.contacts?.errorDeleting || 'Error deleting contact')
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    // Get profile type label
    const getProfileTypeLabel = (type) => {
        const langKey = `name_${language}`;
        return type[langKey] || type.name_fr || type.name_en || type.code || '';
    };

    // Tab definitions
    const tabs = [
        { id: 0, icon: User, label: t.contacts?.tabs?.businessCard || 'Info' },
        { id: 1, icon: MapPin, label: t.contacts?.tabs?.address || 'Address' },
        { id: 2, icon: Briefcase, label: t.contacts?.tabs?.professional || 'Pro' },
        { id: 3, icon: FileText, label: t.contacts?.tabs?.notes || 'Notes' }
    ];

    // Check if contact is public (linked to people)
    const isPublicContact = !!contact?.peopleuuid;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink p-4 flex items-center justify-between">
                    <h2 className="text-white text-lg font-semibold">
                        {t.contacts?.editContact || 'Edit Contact'}
                    </h2>
                    <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded-full transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Public contact warning */}
                {isPublicContact && (
                    <div className="mx-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        {t.contacts?.publicContactWarning || 'This contact is linked to a public profile. Some information comes from that profile.'}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-2 text-sm font-medium transition ${
                                activeTab === tab.id
                                    ? 'text-cabaza-purple border-b-2 border-cabaza-purple bg-purple-50'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="p-4 overflow-y-auto max-h-[50vh]">
                    {/* Tab 0: Basic Info */}
                    {activeTab === 0 && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.firstName || 'First Name'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => handleFieldChange('firstName', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.lastName || 'Last Name'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.gender || 'Gender'}
                                    </label>
                                    <select
                                        value={formData.sexe}
                                        onChange={(e) => handleFieldChange('sexe', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    >
                                        {sexeOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">{t.contacts?.fields?.email || 'Email'}</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleFieldChange('email', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.phoneMobile || 'Phone'}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.phoneFixed || 'Office Phone'}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phoneBureau}
                                        onChange={(e) => handleFieldChange('phoneBureau', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.contacts?.fields?.company || 'Company'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => handleFieldChange('company', e.target.value)}
                                    disabled={isPublicContact}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                />
                            </div>
                        </div>
                    )}

                    {/* Tab 1: Address */}
                    {activeTab === 1 && (
                        <div className="space-y-4">
                            {isGoogleMapsLoaded ? (
                                <AddressAutocomplete
                                    label={t.register?.address?.searchAddress || 'Search address'}
                                    value={formData.googleAddress}
                                    onChange={(field, value) => handleFieldChange('googleAddress', value)}
                                    onCoordinatesChange={handleCoordinatesChange}
                                    onAddressComponentsChange={handleAddressComponentsChange}
                                    apiKey={googleMapsApiKey}
                                    disabled={isPublicContact}
                                />
                            ) : (
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.tabs?.address || 'Address'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.googleAddress}
                                        onChange={(e) => handleFieldChange('googleAddress', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.street || 'Street'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.street}
                                        onChange={(e) => handleFieldChange('street', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">{t.contacts?.fields?.number || 'N°'}</label>
                                    <input
                                        type="text"
                                        value={formData.number}
                                        onChange={(e) => handleFieldChange('number', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.box || 'Box'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.box}
                                        onChange={(e) => handleFieldChange('box', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.postalCode || 'Postal Code'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.postalCode}
                                        onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.city || 'City'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => handleFieldChange('city', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.country || 'Country'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.country}
                                        onChange={(e) => handleFieldChange('country', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.register?.address?.nationality || 'Nationality'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.nationality}
                                        onChange={(e) => handleFieldChange('nationality', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            </div>

                            {formData.latitude && formData.longitude && (
                                <div className="text-sm text-green-600 flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    {t.register?.address?.gpsRecorded || 'GPS coordinates saved'}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 2: Professional */}
                    {activeTab === 2 && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.register?.profileType?.sectionTitle || 'Profile Type'}
                                    </label>
                                    <select
                                        value={formData.profileTypeId}
                                        onChange={(e) => handleFieldChange('profileTypeId', e.target.value)}
                                        disabled={isLoadingProfileTypes || isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    >
                                        <option value="">{t.common?.select || 'Select...'}</option>
                                        {profileTypes.map(type => (
                                            <option key={type.id || type.ID} value={type.id || type.ID}>
                                                {getProfileTypeLabel(type)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.register?.employmentStatus?.title || 'Status'}
                                    </label>
                                    <select
                                        value={formData.employmentStatus ? 'active' : 'inactive'}
                                        onChange={(e) => handleFieldChange('employmentStatus', e.target.value === 'active')}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    >
                                        <option value="active">{t.endroits?.active || 'Active'}</option>
                                        <option value="inactive">{t.products?.inactive || 'Inactive'}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Occupation search */}
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.contacts?.fields?.profession || 'Occupation'}
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={occupationQuery}
                                        onChange={(e) => handleOccupationSearch(e.target.value)}
                                        placeholder={t.register?.professional?.professionPlaceholder || 'Search occupation...'}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                    {isSearchingOccupation && (
                                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cabaza-purple animate-spin" />
                                    )}
                                </div>
                                {showOccupationResults && occupationResults.length > 0 && (
                                    <div className="mt-1 border border-gray-200 rounded-lg max-h-32 overflow-y-auto">
                                        {occupationResults.map((occ, idx) => (
                                            <div
                                                key={occ.uri || idx}
                                                className="p-2 hover:bg-gray-50 cursor-pointer text-sm"
                                                onClick={() => handleSelectOccupation(occ)}
                                            >
                                                {occ.displayLabel || occ.title}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedOccupation && (
                                    <div className="mt-1 text-sm text-green-600 flex items-center gap-1">
                                        <Check className="w-4 h-4" />
                                        {t.common?.success || 'Selected'}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.endroits?.tvaNum || 'VAT Number'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.tvaNum}
                                    onChange={(e) => handleFieldChange('tvaNum', e.target.value)}
                                    placeholder="BE0123456789"
                                    disabled={isPublicContact}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.common?.bank || 'Bank'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.bankName}
                                        onChange={(e) => handleFieldChange('bankName', e.target.value)}
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">IBAN</label>
                                    <input
                                        type="text"
                                        value={formData.bankNum}
                                        onChange={(e) => handleFieldChange('bankNum', e.target.value)}
                                        placeholder="BE00 0000 0000 0000"
                                        disabled={isPublicContact}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple disabled:bg-gray-100"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.common?.reputation || 'Reputation'}
                                </label>
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => handleFieldChange('reputation', star)}
                                            className="p-1"
                                        >
                                            <Star
                                                className={`w-6 h-6 ${
                                                    star <= formData.reputation
                                                        ? 'text-yellow-400 fill-yellow-400'
                                                        : 'text-gray-300'
                                                }`}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab 3: Notes */}
                    {activeTab === 3 && (
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t.contacts?.fields?.notes || 'Notes'}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleEnhanceDescription}
                                        disabled={isEnhancingDescription || !formData.description.trim()}
                                        className="flex items-center gap-1 text-xs text-cabaza-purple hover:text-cabaza-pink disabled:opacity-50"
                                    >
                                        {isEnhancingDescription ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <Sparkles className="w-3 h-3" />
                                        )}
                                        {t.contacts?.ai?.enhance || 'Enhance with AI'}
                                    </button>
                                </div>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    rows={8}
                                    placeholder={t.contacts?.fields?.notesPlaceholder || 'Personal notes, contact description...'}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple resize-none"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    {saveMessage.text && (
                        <div className={`mb-3 p-3 rounded-lg text-sm flex items-center gap-2 ${
                            saveMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                            {saveMessage.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            {saveMessage.text}
                        </div>
                    )}

                    {/* Delete confirmation */}
                    {showDeleteConfirm ? (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700 mb-2">
                                {t.contacts?.deleteWarning || 'Are you sure you want to delete this contact?'}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={handleDelete}
                                    loading={isDeleting}
                                >
                                    {t.contacts?.actions?.delete || 'Delete'}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowDeleteConfirm(false)}
                                >
                                    {t.contacts?.actions?.cancel || t.common?.cancel || 'Cancel'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-3">
                            <Button
                                variant="danger"
                                onClick={() => setShowDeleteConfirm(true)}
                                icon={Trash2}
                            >
                                {t.contacts?.actions?.delete || 'Delete'}
                            </Button>
                            <div className="flex-1" />
                            <Button variant="outline" onClick={onClose}>
                                {t.contacts?.actions?.cancel || t.common?.cancel || 'Cancel'}
                            </Button>
                            <Button onClick={handleSave} loading={isSaving} icon={Save}>
                                {t.contacts?.actions?.save || t.common?.save || 'Save'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditContactModal;
